import {
  usePortalDailyReportReviewMutation,
  usePortalManageWorkQuery,
  usePortalTaskCancelMutation,
  usePortalTaskCreateMutation,
  usePortalTaskReviewMutation,
  usePortalTaskTemplateSaveMutation,
} from "@dashboard/graphql";
import {
  Box,
  Button,
  Checkbox,
  Input,
  Option,
  Select,
  Text,
  Textarea,
} from "@saleor/macaw-ui-next";
import { useState } from "react";
import { useIntl } from "react-intl";

import { ErrorText, InlineText, Page, Section } from "../components";
import { portalLabels as l } from "../labels";
import { portalMessages as m } from "../messages";
import { useTaskStatusLabel } from "../taskLabels";
import { formatDateTime, formatDay, lisbonToIso, today, weekdayShort } from "../utils";

const ReviewButtons = ({
  onApprove,
  onReturn,
}: {
  onApprove: () => void;
  onReturn: (note: string) => void;
}) => {
  const intl = useIntl();
  const [returning, setReturning] = useState(false);

  return returning ? (
    <Box width="100%">
      <InlineText
        placeholder={intl.formatMessage(m.whatIsMissing)}
        onCancel={() => setReturning(false)}
        onSubmit={note => {
          setReturning(false);
          onReturn(note);
        }}
      />
    </Box>
  ) : (
    <>
      <Button size="small" variant="primary" onClick={onApprove}>
        {intl.formatMessage(m.accept)}
      </Button>
      <Button size="small" variant="secondary" onClick={() => setReturning(true)}>
        {intl.formatMessage(m.returnIt)}
      </Button>
    </>
  );
};

export const ManageTasks = () => {
  const intl = useIntl();
  const statusLabel = useTaskStatusLabel();
  const { data, refetch } = usePortalManageWorkQuery({ fetchPolicy: "cache-and-network" });
  const [review, reviewOpts] = usePortalTaskReviewMutation({ onCompleted: () => refetch() });
  const [cancel] = usePortalTaskCancelMutation({ onCompleted: () => refetch() });
  const [reviewReport, reportOpts] = usePortalDailyReportReviewMutation({
    onCompleted: () => refetch(),
  });
  const emptyTask = { assigneeId: "", title: "", description: "", day: today(), time: "17:00" };
  const [form, setForm] = useState(emptyTask);
  const [create, createOpts] = usePortalTaskCreateMutation({
    onCompleted: result => {
      if (!result.taskCreate?.errors.length) {
        setForm(emptyTask);
        refetch();
      }
    },
  });
  const emptyTemplate = {
    id: "",
    title: "",
    description: "",
    recurrence: "daily",
    weekday: 0,
    monthDay: 1,
    dueTime: "",
    assigneeIds: [] as string[],
    isActive: true,
  };
  const [template, setTemplate] = useState(emptyTemplate);
  const [saveTemplate, templateOpts] = usePortalTaskTemplateSaveMutation({
    onCompleted: result => {
      if (!result.taskTemplateSave?.errors.length) {
        setTemplate(emptyTemplate);
        refetch();
      }
    },
  });
  const people: Option[] = (data?.employeeProfiles ?? []).map(p => ({
    value: p.userId,
    label: p.fullName,
  }));
  const recurrenceOptions: Option[] = [
    { value: "daily", label: intl.formatMessage(l.recurrenceDaily) },
    { value: "weekly", label: intl.formatMessage(l.recurrenceWeekly) },
    { value: "monthly", label: intl.formatMessage(l.recurrenceMonthly) },
  ];

  return (
    <Page>
      <Section title={intl.formatMessage(m.toApprove)}>
        {(data?.tasks ?? []).length === 0 && (
          <Text size={2} color="default2">
            {intl.formatMessage(m.noAlerts)}
          </Text>
        )}
        {data?.tasks.map(task => (
          <Box key={task.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Text size={2}>
              <b>{task.assigneeName}</b> · {task.title} ·{" "}
              {intl.formatMessage(m.dueAt, { when: formatDateTime(task.dueAt) })}
              {task.doneNote && ` — “${task.doneNote}”`}
            </Text>
            <ReviewButtons
              onApprove={() => review({ variables: { id: task.id, approve: true } })}
              onReturn={note => review({ variables: { id: task.id, approve: false, note } })}
            />
          </Box>
        ))}
        <ErrorText errors={reviewOpts.data?.taskReview?.errors} />
      </Section>

      <Section title={intl.formatMessage(m.reportsToReview)}>
        {(data?.dailyReports ?? []).length === 0 && (
          <Text size={2} color="default2">
            {intl.formatMessage(m.noAlerts)}
          </Text>
        )}
        {data?.dailyReports.map(report => (
          <Box key={report.id} display="flex" flexDirection="column" gap={1} paddingY={1}>
            <Text size={3}>
              <b>{report.employeeName}</b> · {formatDay(report.date)}
            </Text>
            <Text size={2} __whiteSpace="pre-wrap">
              {report.text}
            </Text>
            <Box display="flex" gap={2} flexWrap="wrap">
              <ReviewButtons
                onApprove={() => reviewReport({ variables: { id: report.id, approve: true } })}
                onReturn={note =>
                  reviewReport({ variables: { id: report.id, approve: false, note } })
                }
              />
            </Box>
          </Box>
        ))}
        <ErrorText errors={reportOpts.data?.dailyReportReview?.errors} />
      </Section>

      <Section title={intl.formatMessage(m.newTask)}>
        <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))">
          <Select
            size="small"
            label={intl.formatMessage(m.assignee)}
            options={people}
            value={people.find(o => o.value === form.assigneeId) ?? null}
            onChange={option => setForm({ ...form, assigneeId: option.value })}
          />
          <Input
            size="small"
            label={intl.formatMessage(m.title)}
            value={form.title}
            maxLength={200}
            onChange={e => setForm({ ...form, title: e.target.value })}
          />
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(m.date)}
            value={form.day}
            onChange={e => setForm({ ...form, day: e.target.value })}
          />
          <Input
            size="small"
            type="time"
            label={intl.formatMessage(m.eta)}
            value={form.time}
            onChange={e => setForm({ ...form, time: e.target.value })}
          />
        </Box>
        <Textarea
          rows={3}
          label={intl.formatMessage(m.text)}
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
        />
        <Box>
          <Button
            size="small"
            variant="primary"
            disabled={!form.assigneeId || !form.title.trim() || createOpts.loading}
            onClick={() =>
              create({
                variables: {
                  assigneeId: form.assigneeId,
                  title: form.title.trim(),
                  description: form.description,
                  dueAt: lisbonToIso(form.day, form.time),
                },
              })
            }
          >
            {intl.formatMessage(m.assign)}
          </Button>
        </Box>
        <Text size={1} color="default2">
          {intl.formatMessage(m.etaHint)}
        </Text>
        <ErrorText errors={createOpts.data?.taskCreate?.errors} />
      </Section>

      <Section title={intl.formatMessage(m.openTasks)}>
        {data?.openTasks.map(task => (
          <Box key={task.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Text size={2}>
              <b>{task.assigneeName}</b> · {task.title} ·{" "}
              {intl.formatMessage(m.dueAt, { when: formatDateTime(task.dueAt) })} ·{" "}
              {statusLabel(task.status)}
            </Text>
            <Button
              size="small"
              variant="tertiary"
              onClick={() => cancel({ variables: { id: task.id } })}
            >
              {intl.formatMessage(m.cancel)}
            </Button>
          </Box>
        ))}
      </Section>

      <Section title={intl.formatMessage(m.recurringTasks)}>
        {data?.taskTemplates.map(t => (
          <Box key={t.id} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Text size={2} color={t.isActive ? undefined : "default2"}>
              <b>{t.title}</b> · {recurrenceOptions.find(o => o.value === t.recurrence)?.label}
              {t.recurrence === "weekly" && t.weekday !== null
                ? ` (${weekdayShort(intl.locale, t.weekday)})`
                : ""}
              {t.recurrence === "monthly" ? ` (${t.monthDay})` : ""}
              {t.dueTime ? ` · ${t.dueTime}` : ""} · {t.assigneeNames.join(", ")}
            </Text>
            <Button
              size="small"
              variant="tertiary"
              onClick={() =>
                setTemplate({
                  id: t.id,
                  title: t.title,
                  description: t.description,
                  recurrence: t.recurrence,
                  weekday: t.weekday ?? 0,
                  monthDay: t.monthDay ?? 1,
                  dueTime: t.dueTime ?? "",
                  assigneeIds: t.assigneeIds,
                  isActive: t.isActive,
                })
              }
            >
              ✎
            </Button>
          </Box>
        ))}
        <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(170px, 1fr))">
          <Input
            size="small"
            label={intl.formatMessage(m.title)}
            value={template.title}
            maxLength={200}
            onChange={e => setTemplate({ ...template, title: e.target.value })}
          />
          <Select
            size="small"
            label={intl.formatMessage(l.recurrence)}
            options={recurrenceOptions}
            value={recurrenceOptions.find(o => o.value === template.recurrence) ?? null}
            onChange={option => setTemplate({ ...template, recurrence: option.value })}
          />
          {template.recurrence === "weekly" && (
            <Select
              size="small"
              label={intl.formatMessage(l.weekday)}
              options={[0, 1, 2, 3, 4, 5, 6].map(d => ({
                value: String(d),
                label: weekdayShort(intl.locale, d),
              }))}
              value={{
                value: String(template.weekday),
                label: weekdayShort(intl.locale, template.weekday),
              }}
              onChange={option => setTemplate({ ...template, weekday: Number(option.value) })}
            />
          )}
          {template.recurrence === "monthly" && (
            <Input
              size="small"
              type="number"
              min={1}
              max={31}
              label={intl.formatMessage(l.monthDay)}
              value={String(template.monthDay)}
              onChange={e => setTemplate({ ...template, monthDay: Number(e.target.value) })}
            />
          )}
          <Input
            size="small"
            type="time"
            label={intl.formatMessage(m.eta)}
            value={template.dueTime}
            onChange={e => setTemplate({ ...template, dueTime: e.target.value })}
          />
        </Box>
        <Box display="flex" gap={3} flexWrap="wrap">
          {people.map(person => (
            <Checkbox
              key={person.value}
              checked={template.assigneeIds.includes(person.value)}
              onCheckedChange={checked =>
                setTemplate({
                  ...template,
                  assigneeIds: checked
                    ? [...template.assigneeIds, person.value]
                    : template.assigneeIds.filter(id => id !== person.value),
                })
              }
            >
              <Text size={2}>{person.label}</Text>
            </Checkbox>
          ))}
        </Box>
        <Textarea
          rows={2}
          label={intl.formatMessage(m.text)}
          value={template.description}
          onChange={e => setTemplate({ ...template, description: e.target.value })}
        />
        <Box display="flex" gap={2} alignItems="center">
          <Checkbox
            checked={template.isActive}
            onCheckedChange={c => setTemplate({ ...template, isActive: c === true })}
          >
            <Text size={2}>{intl.formatMessage(l.active)}</Text>
          </Checkbox>
          <Button
            size="small"
            variant="primary"
            disabled={
              !template.title.trim() || !template.assigneeIds.length || templateOpts.loading
            }
            onClick={() =>
              saveTemplate({
                variables: {
                  id: template.id || undefined,
                  input: {
                    title: template.title.trim(),
                    description: template.description,
                    recurrence: template.recurrence,
                    weekday: template.weekday,
                    monthDay: template.monthDay,
                    dueTime: template.dueTime || null,
                    assigneeIds: template.assigneeIds,
                    isActive: template.isActive,
                  },
                },
              })
            }
          >
            {intl.formatMessage(m.save)}
          </Button>
        </Box>
        <ErrorText errors={templateOpts.data?.taskTemplateSave?.errors} />
      </Section>
    </Page>
  );
};
